import Parse from "parse";
import React from "react";

import { $parse } from "../state";
import { AnyViewType } from "../types";

import { ParseFilterState } from "../state/ParseFilterState";

import { ViewLayout, onRow } from "./views/ViewLayout";

import { createContext } from "react";
import { CanbanView } from "../features/KanbanView/components/CanbanView";
import { CalendarView } from "./views/CalendarView";
import { CardView } from "./views/CardView";
import { ListView } from "./views/ListView";
import { TableView } from "./views/TableView";

type Props = {
  className: string;
  pagination?: number;
  view?: AnyViewType;
  state?: ParseFilterState;
  beforeCreate?: () => Parse.Object | null;
  afterCreate?: (objectId: string) => void;
  beforeDelete?: (objectId: string[]) => Promise<boolean>;
  afterDelete?: (objectId: string[]) => void;
  hideCreate?: boolean;
  hideEdit?: boolean;
  hideActionBar?: boolean;
  onRow?: onRow;
  headerExtra?: React.ReactNode;
  additionalActions?: React.ReactNode;
  customFilter?: (query: Parse.Query) => void;
};

export const ParseSetViewContext = createContext(null as unknown as Props);
export const ParseSetView = React.memo<Props>(function ParseSetView({
  className,
  view: customView,
  state,
  pagination,
  beforeCreate = $parse.ui.getClassConfig(className)?.beforeCreate,
  afterCreate = $parse.ui.getClassConfig(className)?.afterCreate,
  beforeDelete = $parse.ui.getClassConfig(className)?.beforeDelete,
  afterDelete = $parse.ui.getClassConfig(className)?.afterDelete,
  hideCreate = $parse.ui.getClassConfig(className)?.hideCreate,
  hideEdit,
  hideActionBar,
  onRow = $parse.ui.getClassConfig(className)?.onRow,
  headerExtra = $parse.ui.getDefaultView(className)?.headerextra,
  additionalActions = $parse.ui.getDefaultView(className)?.additionalActions,
  customFilter,
}) {
  const view = customView || $parse.ui.defaultViewMap[className];

  if (!view) {
    return null;
  }

  return (
    <ParseSetViewInner
      className={className}
      view={view}
      state={state}
      beforeCreate={beforeCreate}
      beforeDelete={beforeDelete}
      afterCreate={afterCreate}
      afterDelete={afterDelete}
      hideCreate={hideCreate}
      hideEdit={hideEdit}
      hideActionBar={hideActionBar}
      onRow={onRow}
      pagination={pagination}
      headerExtra={headerExtra}
      additionalActions={additionalActions}
      customFilter={customFilter}
    />
  );
});

const ParseSetViewInner = React.memo<Props>(function ParseSetViewInner({
  className,
  view,
  pagination,
  state: customState,
  beforeCreate,
  afterCreate,
  beforeDelete,
  afterDelete,
  hideCreate,
  hideEdit,
  hideActionBar,
  onRow,
  headerExtra,
  customFilter,
  additionalActions,
}) {
  const state = React.useMemo(() => {
    if (customState) {
      return customState;
    }
    const cState = $parse.ui.createFilterState(className, view, true);
    cState.setCustomQuery(customFilter);
    return cState;
  }, [className, view, customState, customFilter]);

  return (
    <ParseSetViewContext.Provider
      value={{
        className,
        view,
        state,
        beforeCreate,
        afterCreate,
        beforeDelete,
        afterDelete,
        hideCreate,
        hideEdit,
        hideActionBar,
        onRow,
        headerExtra,
        additionalActions,
        customFilter,
      }}
    >
      <ViewLayout
        state={state}
        view={view}
        beforeCreate={beforeCreate}
        beforeDelete={beforeDelete}
        afterCreate={afterCreate}
        afterDelete={afterDelete}
        hideCreate={hideCreate}
        hideEdit={hideEdit}
        hideActionBar={hideActionBar}
        onRow={onRow}
        headerExtra={headerExtra}
        additionalActions={additionalActions}
      >
        <ParseSetViewSwitch
          className={className}
          pagination={pagination}
          state={state}
          view={view}
          onRow={onRow}
          hideEdit={hideEdit}
        />
      </ViewLayout>
    </ParseSetViewContext.Provider>
  );
});

const ParseSetViewSwitch = React.memo<Props>(function ParseSetViewSwitch({
  view,
  pagination,
  state,
  onRow,
  hideEdit,
}) {
  switch (view.type) {
    case "table":
      return (
        <TableView
          state={state}
          pagination={pagination}
          view={view}
          onRow={onRow}
          hideEdit={hideEdit}
        />
      );
    case "list":
      return <ListView state={state} view={view} />;
    case "calendar":
      return <CalendarView state={state} view={view} />;
    case "card":
      return <CardView state={state} view={view} />;
    case "canban":
      return <CanbanView state={state} view={view} />;
    default:
      return null;
  }
});
